import { PAGE_ROUTES } from "@app/pages/pages-routes";
import { ExternalFeatureCategories, ExternalFeatureCode, PermissionTypeDto } from "clearline-api";
import { RoleTypes } from "core-kit";
import { NavMenuItem, ParentMenuIdentifier, SidebarAction, SidebarItemOptionsParam } from "./nav-menu";

// SUPERADMIN level base items
const superadminBaseDashboard: NavMenuItem = {
  label: "Dashboard",
  icon: "ri-home-2-line",
  routePath: "/"
};

const superadminBaseActivationCodes: NavMenuItem = {
  label: "ActivationCodes",
  icon: "ri-hashtag",
  routePath: "/activationCodes",
  options: { pathWithParam: true }
};

const superadminBaseSalesAgents: NavMenuItem = {
  label: "Sales Agents",
  icon: "ri-user-2-line",
  routePath: "/admin/salesAgents"
};

const superadminBaseBrands: NavMenuItem = {
  label: "Brands",
  icon: "ri-vip-crown-line",
  routePath: "/admin/brands"
};

const superadminBaseAccounts: NavMenuItem = {
  label: "accounts",
  icon: "ri-account-box-line",
  routePath: "/admin/accounts"
};

const superadminBaseLocations: NavMenuItem = {
  label: "Locations",
  icon: "ri-store-2-line",
  routePath: "/admin/locations"
};

const superadminBaseUsers: NavMenuItem = {
  label: "Users",
  icon: "ri-user-line",
  routePath: "/admin/users"
};

const superadminBaseImport: NavMenuItem = {
  label: "Import",
  routePath: "/admin/import",
  icon: "bx bx-import"
};

const superadminBaseUserApplications: NavMenuItem = {
  label: "User Applications",
  subItems: [
    {
      label: "2 REWARD ME",
      icon: "ri-edit-circle-line",
      action: SidebarAction.OpenPlatform,
      options: { params: SidebarItemOptionsParam.TwoReward }
    },
    {
      // `featureCode` lets a partner admin's auto login URL from the API replace this link at runtime.
      label: "SMS USA",
      icon: "ri-edit-circle-line",
      action: SidebarAction.OpenPlatform,
      options: { params: SidebarItemOptionsParam.SmsUSA, featureCode: ExternalFeatureCode.GlobalSms }
    },
    {
      label: "SMS Global",
      icon: "ri-edit-circle-line",
      action: SidebarAction.OpenPlatform,
      options: { params: SidebarItemOptionsParam.SmsGlobal }
    }
  ]
};

// MENU CONFIGURATIONS BY ROLES
const superAdminMenu: NavMenuItem[] = [
  superadminBaseDashboard,
  {
    label: "New registrations",
    icon: "ri-user-add-line",
    routePath: "/admin/newRegistrations"
  },
  superadminBaseActivationCodes,
  {
    label: "Partners",
    icon: "ri-user-star-line",
    routePath: "/partner",
    options: { pathWithParam: true }
  },
  superadminBaseSalesAgents,
  superadminBaseBrands,
  superadminBaseAccounts,
  superadminBaseLocations,
  {
    label: "Landing Pages",
    icon: "ri-newspaper-line",
    routePath: "/admin/landings"
  },
  superadminBaseUsers,
  {
    label: "Marketing Tools",
    icon: "ri-settings-3-line",
    subItems: [
      {
        label: "Global Marketing Templates",
        routePath: "/admin/templates"
      },
      {
        label: "Bulk Links",
        routePath: "/admin/bulk-links"
      }
    ]
  },
  {
    label: "ReportingAnalytics",
    icon: "ri-bar-chart-2-fill",
    subItems: [
      {
        label: "TopCompaniesReport",
        routePath: "/" + PAGE_ROUTES.REPORT_TOP_COMPANIES
      },
      {
        label: "TopPartnersReport",
        routePath: "/" + PAGE_ROUTES.REPORT_TOP_PARTNERS
      },
      {
        label: "TopLocationsReport",
        routePath: "/" + PAGE_ROUTES.REPORT_TOP_LOCATIONS
      },
      {
        label: "ShortLinksReport",
        routePath: "/" + PAGE_ROUTES.REPORT_SHORT_LINKS
      }
    ]
  },
  {
    label: "Applications",
    icon: "ri-command-line",
    subItems: [
      superadminBaseUserApplications,
      {
        label: "Admin Applications",
        subItems: [
          {
            label: "Manage Accounts",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://partners.vendasta.com/", newTab: true }
          },
          {
            label: "Manage Products",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://vendors.vendasta.com/products", newTab: true }
          },
          {
            label: "Sales & Success",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://clearline-mobile.snapshotreport.biz/", newTab: true }
          },
          {
            label: "Retention Center",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://clearline-mobile.smblogin.com/account/location", newTab: true }
          },
          {
            label: "Task Manager",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://task-manager.biz/partner/FOH9/my-work/tasks", newTab: true }
          },
          {
            label: "Billing Management",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://app.chargebee.com/login", newTab: true }
          },
          {
            label: "Kiosk App MDM",
            icon: "ri-edit-circle-line",
            action: SidebarAction.OpenHyperLink,
            options: { hyperLink: "https://cloud.fully-kiosk.com/cloud/", newTab: true }
          }
        ]
      }
    ]
  },
  {
    label: "Widget management",
    icon: "ri-apps-line",
    subItems: [
      {
        label: "Campaign configuration",
        routePath: "/campaign-configuration"
      }
    ]
  },
  superadminBaseImport,
  {
    label: "External features",
    icon: "ri-briefcase-4-line",
    routePath: "/admin/configured-external-features"
  }
];

const partnerAdminMenu: NavMenuItem[] = [
  superadminBaseDashboard,
  {
    label: "New registrations",
    icon: "ri-user-add-line",
    routePath: "/admin/newRegistrations"
  },
  superadminBaseActivationCodes,
  superadminBaseSalesAgents,
  superadminBaseBrands,
  superadminBaseAccounts,
  superadminBaseLocations,
  superadminBaseUsers,
  {
    label: "Marketing Tools",
    icon: "ri-settings-3-line",
    subItems: [
      {
        label: "Global Marketing Templates",
        routePath: "/admin/templates"
      }
    ]
  },
  {
    label: "Partner Profile",
    icon: "ri-user-star-line",
    subItems: [
      {
        label: "Partner Details",
        routePath: "/partner/details"
      },
      {
        label: "Partner Admins",
        routePath: "/partner/users"
      },
      {
        label: "Delivery Settings",
        routePath: "/partner/delivery"
      }
    ]
  },
  {
    label: "Applications",
    icon: "ri-command-line",
    subItems: [superadminBaseUserApplications]
  },
  superadminBaseImport
];

const accountAdminMenu: NavMenuItem[] = [
  // 1 - Dashboard
  {
    label: "Dashboard",
    identifier: PermissionTypeDto.Dashboard,
    hasInfoTooltip: true,
    icon: "ri-home-2-line",
    routePath: "/"
  },
  // 2 - Marketing Center
  {
    label: "Marketing Center",
    identifier: PermissionTypeDto.Cmc,
    hasInfoTooltip: true,
    icon: "ri-megaphone-line",
    action: SidebarAction.OpenCmc,
    options: { newTab: true },
    hidden: true
  },
  // 3 - Marketing Tools (with nested submenus)
  {
    label: "Marketing Tools",
    icon: "ri-settings-3-line",
    hidden: true,
    subItems: [
      // 3.1 - Smart Pages
      {
        label: "Smart Pages",
        subItems: [
          {
            label: "MappSettings",
            identifier: PermissionTypeDto.MarketingTools,
            hasInfoTooltip: true,
            routePath: "/manager/app-settings"
          },
          {
            label: "Templates",
            identifier: PermissionTypeDto.Templates,
            hasInfoTooltip: true,
            routePath: "/manager/templates"
          },
          {
            label: "Terminals",
            identifier: PermissionTypeDto.CfsTerminals,
            hasInfoTooltip: true,
            routePath: "/manager/locations/cfsterminals",
            options: { pathWithParam: true }
          }
        ]
      },
      // 3.2 - Clearline Sites
      {
        label: "Clearline Sites",
        identifier: ParentMenuIdentifier.Sites,
        subItems: [
          {
            label: "SitesBuilder",
            action: SidebarAction.OpenExternalFeature,
            options: { newTab: true, params: SidebarItemOptionsParam.SitesBuilder },
            identifier: PermissionTypeDto.SitesBuilder,
            hasInfoTooltip: true
          }
        ]
      },
      // 3.3 - Loyalty Program
      {
        label: "LoyaltyProgram",
        subItems: [
          {
            label: "LoyaltyProgram",
            identifier: PermissionTypeDto.LoyaltyProgram,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.LoyaltyProgram },
            hidden: true
          },
          {
            label: "Launch Landing Pages",
            identifier: PermissionTypeDto.LaunchLandingPages,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.LaunchLandingPages },
            hidden: true
          },
          {
            label: "Program Statistics",
            identifier: PermissionTypeDto.LoyaltyProgramStatistics,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.LoyaltyProgramStatistics },
            hidden: true
          },
          {
            label: "Registered users",
            identifier: PermissionTypeDto.LoyaltyProgramRegisteredUsers,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.LoyaltyProgramRegisteredUsers },
            hidden: true
          }
        ]
      },
      // 3.4 - Digital Coupons
      {
        label: "Digital Coupons",
        subItems: [
          {
            label: "Create Vouchers & Deals",
            identifier: PermissionTypeDto.CreateVouchersAndDeals,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.CreateVouchersAndDeals },
            hidden: true
          },
          {
            label: "Coupons",
            identifier: PermissionTypeDto.Coupons,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.Coupons },
            hidden: true
          },
          {
            label: "GamifiedCoupons",
            identifier: PermissionTypeDto.GamifiedCoupons,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.GamifiedCoupons },
            hidden: true
          },
          {
            label: "Unique Code Generator",
            identifier: PermissionTypeDto.UniqueCodeGenerator,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.UniqueCodeGenerator },
            hidden: true
          },
          {
            label: "Digital Coupons Catalog",
            identifier: PermissionTypeDto.DigitalCouponsCatalogue,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.DigitalCouponsCatalogue },
            hidden: true
          },
          {
            label: "FlowAutomation",
            identifier: PermissionTypeDto.FlowAutomation,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.FlowAutomation },
            hidden: true
          }
        ]
      },
      // 3.5 - Mobile Marketing
      {
        label: "Mobile Marketing",
        subItems: [
          {
            label: "Mobile Kiosk Software",
            identifier: PermissionTypeDto.MobileKioskSoftware,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.MobileKioskSoftware },
            hidden: true
          },
          {
            label: "Single Use URL Links",
            identifier: PermissionTypeDto.SingleUseUrlLinks,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.SingleUseUrlLinks },
            hidden: true
          },
          {
            label: "Customer Survey",
            identifier: PermissionTypeDto.CustomerSurveyBuilderApp,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.CustomerSurveyBuilderApp },
            hidden: true
          },
          {
            label: "Google Tag Manager",
            identifier: PermissionTypeDto.GoogleTagManager,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.GoogleTagManager },
            hidden: true
          },
          {
            label: "Referral Program",
            identifier: PermissionTypeDto.ReferralProgram,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.ReferralProgram },
            hidden: true
          },
          {
            label: "Birthday Club",
            identifier: PermissionTypeDto.BirthdayClub,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.BirthdayClub },
            hidden: true
          },
          {
            label: "SMS Reminders",
            identifier: PermissionTypeDto.SmsReminders,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.SmsReminders },
            hidden: true
          },
          {
            label: "VIP Club",
            identifier: PermissionTypeDto.VipClub,
            hasInfoTooltip: true,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.VipClub },
            hidden: true
          },
          {
            label: "SMS Marketing Credits",
            identifier: PermissionTypeDto.SmsMarketingCredits,
            hasInfoTooltip: false,
            action: SidebarAction.OpenExternalFeatureByPermission,
            options: { newTab: true, permission: PermissionTypeDto.SmsMarketingCredits },
            hidden: true
          },
          {
            label: "OutboundCampaigns",
            routePath: "/manager/external-features/" + ExternalFeatureCategories[ExternalFeatureCategories.OutboundCampaigns],
            identifier: PermissionTypeDto.OutboundCampaigns,
            hasInfoTooltip: true
          }
        ]
      }
    ]
  },
  // 5 - URL's & QR Codes
  {
    label: "URL's & QR Codes",
    icon: "ri-qr-code-line",
    hidden: true,
    subItems: [
      {
        label: "AppsShortURLs&QRs",
        identifier: PermissionTypeDto.AppsShortUrlsAndQrs,
        hasInfoTooltip: true,
        routePath: "/manager/short-urls/apps"
      },
      {
        label: "CustomQrCodes",
        identifier: PermissionTypeDto.CustomShortUrlsAndQrs,
        hasInfoTooltip: true,
        routePath: "/manager/short-urls/custom"
      },
      {
        label: "Pixel Tags",
        identifier: PermissionTypeDto.TrackingPixel,
        hasInfoTooltip: true,
        routePath: "/manager/pixel-tags"
      },
      {
        label: "Bulk Links",
        identifier: PermissionTypeDto.BulkLinksAndQrsList,
        hasInfoTooltip: true,
        routePath: "/manager/bulk-links"
      },
      {
        label: "NFC Products",
        identifier: PermissionTypeDto.NfcProducts,
        hasInfoTooltip: true,
        routePath: "/manager/nfc-products"
      }
    ]
  },
  // 6 - POS Integration
  {
    label: "POS Integration",
    icon: "ri-book-3-line",
    hidden: true,
    subItems: [
      {
        label: "POS Settings",
        routePath: "/manager/pos-configuration",
        identifier: PermissionTypeDto.PosSettings,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "POS Terminal Modes",
        routePath: "/manager/pos-terminal-modes",
        identifier: PermissionTypeDto.TerminalOperationMode,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "POS Receipt Settings",
        routePath: "/manager/pos-receipt-settings",
        identifier: PermissionTypeDto.PosReceiptSettings,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "Thank You Page Settings",
        routePath: "/manager/pos-thank-you-page",
        identifier: PermissionTypeDto.PosThankYouPage,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "POS Rules",
        routePath: "/manager/pos-rules",
        identifier: PermissionTypeDto.PosRules,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "POS Activity Report",
        routePath: "/manager/pos-activity-report",
        identifier: PermissionTypeDto.PosActivityReport,
        hasInfoTooltip: true,
        hidden: true
      },
      {
        label: "PosMarketplace",
        routePath: "/manager/pos-marketplace",
        identifier: PermissionTypeDto.PosMarketplace,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "Imported Coupons",
        routePath: "manager/imported-coupons",
        identifier: PermissionTypeDto.ImportedPromotions,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "Imported Customers",
        identifier: PermissionTypeDto.Customers,
        hasInfoTooltip: true,
        routePath: "/manager/customers",
        options: { pathWithParam: true },
        hidden: true
      },
      {
        label: "Imported Transactions",
        routePath: "manager/imported-transactions",
        identifier: PermissionTypeDto.ImportedTransactions,
        hasInfoTooltip: false,
        hidden: true
      }
    ]
  },
  // 7 - Apps Marketplace
  {
    label: "Apps Marketplace",
    action: SidebarAction.OpenExternalFeatureByPermission,
    options: { newTab: true, permission: PermissionTypeDto.AppsMarketplace },
    icon: "ri-shopping-bag-2-line",
    identifier: PermissionTypeDto.AppsMarketplace,
    hasInfoTooltip: true,
    hidden: true
  },
  // 8 - Advanced Tools
  {
    label: "Advanced Tools",
    icon: "ri-tools-line",
    hidden: true,
    subItems: [
      {
        label: "Local SEO",
        identifier: PermissionTypeDto.OnlineListingsManagement,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.OnlineListingsManagement },
        hidden: true
      },
      {
        label: "Appointment Booking",
        identifier: PermissionTypeDto.AppointmentBooking,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.AppointmentBooking },
        hidden: true
      },
      {
        label: "Reply to Reviews",
        identifier: PermissionTypeDto.ReplytoReviewsApp,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.ReplytoReviewsApp },
        hidden: true
      },
      {
        label: "Connect on Social",
        identifier: PermissionTypeDto.ConnectOnSocialApp,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.ConnectOnSocialApp },
        hidden: true
      },
      {
        label: "CRM",
        identifier: PermissionTypeDto.Crm,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.Crm },
        hidden: true
      },
      {
        label: "AI Workforce",
        identifier: PermissionTypeDto.AiWorkforce,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.AiWorkforce },
        hidden: true
      },
      {
        label: "Automations",
        identifier: PermissionTypeDto.Automations,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.Automations },
        hidden: true
      }
    ]
  },
  // 9 - Digital Screens & TVs
  {
    label: "Digital Screens",
    identifier: PermissionTypeDto.DigitalScreen,
    hasInfoTooltip: true,
    icon: "ri-tv-2-line",
    action: SidebarAction.OpenDigitalScreensDashboard,
    options: { newTab: true },
    hidden: true
  },
  // 10 - Reporting & Analytics
  {
    label: "ReportingAnalytics",
    icon: "ri-bar-chart-2-fill",
    hidden: true,
    subItems: [
      {
        label: "Reporting Dashboard",
        identifier: PermissionTypeDto.UnifiedReportingDashboard,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.UnifiedReportingDashboard },
        hidden: true
      },
      {
        label: "Executive Report",
        identifier: PermissionTypeDto.ExecutiveReport,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.ExecutiveReport },
        hidden: true
      },
      {
        label: "CustomTransactionReport",
        routePath: "/manager/external-features/" + ExternalFeatureCategories[ExternalFeatureCategories.CustomTransactionReport],
        identifier: PermissionTypeDto.CustomTransactionReport,
        hasInfoTooltip: false, // no text for tooltip yet
        hidden: true
      },
      {
        label: "TerminalActivity",
        routePath: "/" + PAGE_ROUTES.REPORT_TERMINAL_ACTIVITY,
        identifier: PermissionTypeDto.Top20TerminalActivityChart,
        hasInfoTooltip: false, // no text for tooltip yet
        hidden: true
      },
      {
        label: "EngagementsAttemptsReport",
        routePath: "/" + PAGE_ROUTES.REPORT_ENGAGEMENT_ATTEMPTS,
        identifier: PermissionTypeDto.EngagementAttempts,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "TopSalesPerson",
        routePath: "/" + PAGE_ROUTES.REPORT_TOP_SALES_PERSON,
        identifier: PermissionTypeDto.Top10SalesPersonsChart,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "SmartPageActivities",
        routePath: "/" + PAGE_ROUTES.REPORT_SMART_PAGE_ACTIVITIES,
        identifier: PermissionTypeDto.SmartPageChart,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "AppActivitiesReport",
        routePath: "/" + PAGE_ROUTES.REPORT_APP_ACTIVITIES,
        identifier: PermissionTypeDto.AppActivitiesChart,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "TotalEngagementsReport",
        routePath: "/" + PAGE_ROUTES.REPORT_ENGAGEMENT_OUTCOMES,
        identifier: PermissionTypeDto.TotalEngagementsChart,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "ActivitiesByLocationsReport",
        routePath: "/" + PAGE_ROUTES.REPORT_ACTIVITIES_BY_LOCATION,
        identifier: PermissionTypeDto.ActivitiesByLocationsChart,
        hasInfoTooltip: false,
        hidden: true
      },
      {
        label: "ShortLinksReport",
        routePath: "/" + PAGE_ROUTES.REPORT_SHORT_LINKS,
        identifier: PermissionTypeDto.ShortLinksChart,
        hasInfoTooltip: false,
        hidden: true
      }
    ]
  },
  // 11 - Company
  {
    label: "Company",
    icon: "ri-building-line",
    hidden: true,
    subItems: [
      {
        label: "Contacts",
        identifier: PermissionTypeDto.ExternalContacts,
        hasInfoTooltip: true,
        routePath: "/manager/external-features/" + ExternalFeatureCategories[ExternalFeatureCategories.Contacts],
        hidden: true
      },
      {
        label: "Products",
        identifier: PermissionTypeDto.Products,
        hasInfoTooltip: true,
        routePath: "/manager/products/location/all",
        hidden: true
      },
      {
        label: "Locations",
        identifier: PermissionTypeDto.Locations,
        hasInfoTooltip: true,
        routePath: "/manager/locations"
      },
      {
        label: "Devices",
        identifier: PermissionTypeDto.Devices,
        hasInfoTooltip: true,
        routePath: "/manager/devices"
      },
      {
        label: "Users",
        identifier: PermissionTypeDto.Users,
        hasInfoTooltip: true,
        routePath: "/manager/users"
      },
      {
        label: "My Brand",
        identifier: PermissionTypeDto.MyBrand,
        hasInfoTooltip: true,
        routePath: "/manager/my-brand"
      },
      {
        label: "PricingPlanUpgrade",
        identifier: PermissionTypeDto.PricingPlanUpgrade,
        hasInfoTooltip: true,
        routePath: "/manager/pricing-plan"
      },
      {
        label: "Settings",
        identifier: PermissionTypeDto.ManagerSettings,
        hasInfoTooltip: true,
        routePath: "/manager/settings",
        hidden: true
      }
    ]
  },
  // 12 - Connectors
  {
    label: "Connectors",
    icon: "ri-stackshare-line",
    hidden: true,
    subItems: [
      {
        label: "Google Business Profile",
        identifier: PermissionTypeDto.GoogleBusinessProfile,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.GoogleBusinessProfile },
        hidden: true
      },
      {
        label: "Facebook",
        identifier: PermissionTypeDto.Facebook,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.Facebook },
        hidden: true
      },
      {
        label: "Google Analytics",
        identifier: PermissionTypeDto.GoogleAnalytics,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.GoogleAnalytics },
        hidden: true
      },
      {
        label: "Google Search Console",
        identifier: PermissionTypeDto.GoogleSearchConsole,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.GoogleSearchConsole },
        hidden: true
      },
      {
        label: "Shopify Storefront",
        identifier: PermissionTypeDto.ShopifyStorefront,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.ShopifyStorefront },
        hidden: true
      },
      {
        label: "Quickbooks Online",
        identifier: PermissionTypeDto.QuickbooksOnline,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.QuickbooksOnline },
        hidden: true
      },
      {
        label: "More Integrations",
        identifier: PermissionTypeDto.MoreIntegrations,
        hasInfoTooltip: true,
        action: SidebarAction.OpenExternalFeatureByPermission,
        options: { newTab: true, permission: PermissionTypeDto.MoreIntegrations },
        hidden: true
      }
    ]
  }
];

export const navMenuItemsByRole = {
  [RoleTypes.Admin]: superAdminMenu,
  [RoleTypes.PartnerAdmin]: partnerAdminMenu,
  [RoleTypes.Manager]: accountAdminMenu,
  [RoleTypes.LocationManager]: accountAdminMenu
};
